import { call, put, takeLatest } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { ApiResponse } from "models";
import { BrandPayload } from "models/brand";
import invoiceApi from "api/inVoiceApi";
import { invoiceAction } from "./inVoiceSlice";

interface GetInvoicePdfPayload {
  invoiceId: string;
}
function* getInvoiceList(action: PayloadAction<BrandPayload>) {
  try {
    const response: ApiResponse = yield call(
      invoiceApi.getInvoiceList,
      action.payload
    );
    const data: ApiResponse = response.data;

    if (data.success) {
      yield put(invoiceAction.getInvoiceSuccess(data));
    } else {
      toast.error(data.message || "Failed to fetch invoice list");
      yield put(invoiceAction.getInvoiceFailed(data));
    }
  } catch (error: any) {
    const errorData = error.response?.data || {
      success: false,
      message: "Something went wrong",
    };
    toast.error(errorData?.message || "Something went wrong");
    yield put(invoiceAction.getInvoiceFailed(errorData));
  }
}
function* voidInvoice(
  action: PayloadAction<{ invoiceId: string; voidReason: string }>
) {
  try {
    const response: ApiResponse = yield call(
      invoiceApi.voidInvoice,
      action.payload.invoiceId,
      action.payload.voidReason
    );
    const data: ApiResponse = response.data;

    if (data.success) {
      toast.success(data.message || "Invoice voided successfully");
      yield put(invoiceAction.voidInvoiceSuccess(data));
    } else {
      toast.error(data.message || "Failed to void invoice");
      yield put(invoiceAction.voidInvoiceFailed(data));
    }
  } catch (error: any) {
    const errorData = error.response?.data || {
      success: false,
      message: "Something went wrong",
    };
    toast.error(errorData?.message || "Something went wrong");
    yield put(invoiceAction.voidInvoiceFailed(errorData));
  }
}

function* deleteInvoice(action: PayloadAction<{ invoiceId: string }>) {
  try {
    const response: ApiResponse = yield call(
      invoiceApi.deleteInvoice,
      action.payload.invoiceId
    );
    const data: ApiResponse = response.data;

    if (data.success) {
      toast.success(data.message || "Invoice deleted successfully");
      yield put(
        invoiceAction.deleteInvoiceSuccess({
          invoiceId: action.payload.invoiceId,
        })
      );
    } else {
      toast.error(data.message || "Failed to delete invoice");
      yield put(invoiceAction.deleteInvoiceFailed(data));
    }
  } catch (error: any) {
    const errorData = error.response?.data || {
      success: false,
      message: "Something went wrong",
    };
    toast.error(errorData?.message || "Something went wrong");
    yield put(invoiceAction.deleteInvoiceFailed(errorData));
  }
}

export function* getInvoicePdfSaga(
  action: PayloadAction<{ invoiceId: string }>
) {
  try {
    const response: ApiResponse = yield call(
      invoiceApi.getInvoicePdf,
      action.payload.invoiceId
    );
    const data: any = response.data;
    if (data.success) {
      toast.success(data.message);
      yield put(invoiceAction.getInvoicePdfSuccess(data));
    } else {
      toast.error(data.message);
      yield put(invoiceAction.getInvoicePdfFailed(data));
    }
  } catch (error: any) {
    const errorData: ApiResponse = error.response?.data || {
      success: false,
      message: "Failed to fetch invoice PDF",
    };

    toast.error(errorData?.message || "Something went wrong");
    yield put(invoiceAction.getInvoicePdfFailed(errorData));
  }
}

function* updateInvoice(action: PayloadAction<any>) {
  try {
    const response: ApiResponse = yield call(
      invoiceApi.updateInvoice,
      action.payload
    );
    const data: ApiResponse = response.data;
    if (data.success) {
      toast.success(data.message);
      yield put(invoiceAction.updateInvoiceSuccess(data));
    }
  } catch (error: any) {
    const errorData = error.response?.data;
    toast.error(errorData?.message || "Something went wrong");
    yield put(invoiceAction.updateInvoiceFailed(errorData));
  }
}

function* sendInvoice(
  action: PayloadAction<{ invoiceId: string; isEmail: boolean }>
) {
  try {
    const response: ApiResponse = yield call(
      invoiceApi.sendInvoice,
      action.payload
    );
    const data: ApiResponse = response.data;

    if (data.success) {
      toast.success(data.message);
      yield put(invoiceAction.sendInvoiceSuccess(data));
    }
  } catch (error: any) {
    const errorData = error.response?.data;
    toast.error(errorData?.message || "Failed to send invoice");
    yield put(invoiceAction.sendInvoiceFailed(errorData));
  }
}
function* getPendingInvoices(action: PayloadAction<any>) {
  try {
    const response: ApiResponse = yield call(
      invoiceApi.getPendingInvoices,
      action.payload
    );
    const data: ApiResponse = response.data;

    if (data.success) {
      yield put(invoiceAction.getPendingInvoicesSuccess(data));
    } else {
      toast.error(data.message || "Failed to fetch pending invoices");
      yield put(invoiceAction.getPendingInvoicesFailed(data));
    }
  } catch (error: any) {
    const errorData = error.response?.data || {
      success: false,
      message: "Something went wrong while fetching pending invoices",
    };
    toast.error(errorData?.message || "Something went wrong");
    yield put(invoiceAction.getPendingInvoicesFailed(errorData));
  }
}


function* editInvoice(
  action: PayloadAction<{ invoiceId: string; payload: any }>
) {
  try {
    const { invoiceId, payload } = action.payload;
    const response: ApiResponse = yield call(
      invoiceApi.editInvoice,
      invoiceId,
      payload
    );
    const data: ApiResponse = response.data;
    if (data.success) {
      toast.success(data.message);
      yield put(invoiceAction.editInvoiceSuccess(data));
    }
  } catch (error: any) {
    const errorData = error.response?.data;
    toast.error(errorData?.message || "Something went wrong");
    yield put(invoiceAction.editInvoiceFailed(errorData));
  }
}

export default function* invoiceSaga() {
  yield takeLatest(invoiceAction.getInvoice.type, getInvoiceList);
  yield takeLatest(invoiceAction.updateInvoice, updateInvoice);
  yield takeLatest(invoiceAction.editInvoice, editInvoice);
  yield takeLatest(invoiceAction.voidInvoice.type, voidInvoice);
  yield takeLatest(invoiceAction.deleteInvoice.type, deleteInvoice);
  yield takeLatest(invoiceAction.getInvoicePdfRequest.type, getInvoicePdfSaga);
  yield takeLatest(invoiceAction.sendInvoice.type, sendInvoice);
  yield takeLatest(invoiceAction.getPendingInvoices.type, getPendingInvoices);

}
